import { useCallback } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';

type FleetHealthTemplateModalProps = {
  open: boolean;
  fieldName: string | null;
  options: string[];
  totalCount: number;
  hasOptions: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
};

export const FleetHealthTemplateModal = ({
  open,
  fieldName,
  options,
  totalCount,
  hasOptions,
  onConfirm,
  onCancel,
  onClose,
}: FleetHealthTemplateModalProps) => {
  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title="Fleet Health Template Changed"
      content={
        <Box>
          <Typography
            fontWeight={400}
            fontSize="14px"
            color={abyssColors.primary[500]}
            sx={{ mb: 2 }}
          >
            The Fleet Health template field has been changed. Would you like to generate a new fleet
            health score for this structure?
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: abyssColors.neutral[50],
              border: `1px solid ${abyssColors.neutral[200]}`,
            }}
          >
            {fieldName ? (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: abyssColors.neutral[700],
                    mb: 1,
                  }}
                >
                  Preview: {fieldName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: abyssColors.neutral[600],
                    display: 'block',
                    mb: 1.5,
                    fontStyle: 'italic',
                  }}
                >
                  Following values will be used to calculate the fleet health score:
                </Typography>
                {hasOptions ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: abyssColors.neutral[600], display: 'block', mb: 1 }}
                    >
                      Showing {options.length} of {totalCount} options
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        margin: 0,
                        paddingLeft: 2,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        '& li': {
                          fontSize: '0.875rem',
                          color: abyssColors.neutral[700],
                          py: 0.5,
                        },
                      }}
                    >
                      {options.map((option, index) => (
                        <li key={index}>{option}</li>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: abyssColors.neutral[500], fontStyle: 'italic' }}
                  >
                    This field has no predefined options
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: abyssColors.neutral[700],
                    mb: 1,
                  }}
                >
                  No Category Selected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: abyssColors.neutral[600],
                    display: 'block',
                    fontStyle: 'italic',
                  }}
                >
                  The fleet health score will be generated without any specific category field.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      }
      actions={
        <>
          <Button variant="text" onClick={handleCancel} sx={textButtonStyle}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} sx={containedButtonStyle}>
            Generate Score
          </Button>
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
